import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { DrillDownEvent, DrillDownMapState, DrillDownMessage, ParamsEvent } from '../types'
import i18n from 'live/services/i18n'
import ModalSettingsParameters from './ModalSettingsParameters'
import Bar, { BarValue } from './Bar'
import { createGradientArray } from '../utils/GradiateUtils'
import { calcIntervalDistance, calcTravelledDistance } from '../utils/CalculateUtils'
import { useMarkers } from '../hooks/useMarkers'
import { useSize } from 'ahooks'
import { EventEmitter } from 'ahooks/lib/useEventEmitter'
import { DropdownEmitter, Lookup } from '../DrillDownMap'
import Steps from './Steps'
import { useDeepCompareEffect } from 'ahooks'

type Props = {
    map?: google.maps.Map
    title?: string
    events: DrillDownEvent[]
    paramsEvents: ParamsEvent
    referenceLine?: Lookup
    lookupEntries?: Lookup[]
    metaParamsEvents?: DrillDownEvent
    emitter?: EventEmitter<DropdownEmitter>
    widgetProps?: LiveReactWidgetImplProps<DrillDownMapState, DrillDownMessage>
    selectPolyline: (id: string) => void
}

let intervalDistance: any[] = []

const ParametersInformation = ({
    title = '',
    map,
    lookupEntries,
    referenceLine,
    events,
    widgetProps,
    paramsEvents,
    emitter,
    selectPolyline
}: Props): JSX.Element => {
    const { widget } = widgetProps || {}
    const marker = useRef<google.maps.Marker>(null)
    const { steps } = widget?.jsonConfig || {}

    const toBarValue = (lookup: Lookup, events: DrillDownEvent[]): BarValue[] => {
        const barValues: BarValue[] = []

        events.forEach(event => {
            const { distance = 0, params } = event
            const { color } = params?.find(param => param.lookup === lookup.key) || {}

            barValues.push({ color, distance })
        })

        return barValues
    }

    const buildMainBarValues = (startColor = '#C3E3FA', endColor = '#008AC7'): BarValue[] => {
        const gradientList = createGradientArray(startColor, endColor, events.length)

        const barValues = gradientList.map(color => {
            return { color, distance: 1 }
        })

        return barValues
    }

    const getDrillDowneventActive = (event, ui, width): DrillDownEvent => {
        
        const lastXKm = (ui.lastX * travelledDistance) / width
        const element = intervalDistance.find(ele => lastXKm >= ele.start && lastXKm <= ele.end)

        return events.find(ele => ele.id === element?.marker?.id)
    }

    const _handleDraggableOnDrag = (event, ui, width): void => {
        if (Number.isFinite(ui.lastX)) {
            setCursorPosition({ x: ui.lastX, y: 0 })
        }
    }

    const _createMarker = (event, reCenter = false): void => {
        const aux = { event, url: '/content/plugin-drill-down-map/circle.svg', zIndex: 1 }

        marker.current?.setMap(null)

        createMarkers([aux]).then(res => {
            if (res) {
                const key = Object.keys(res)[0]
                marker.current = res[key]

                const index = events.findIndex(ele => ele.id === event.id)
                const sliceEvents = events.slice(0, index + 1)
                const accumulateDistance = calcTravelledDistance(sliceEvents)
                const x =
                    Number.isFinite(size?.width) &&
                    Number.isFinite(accumulateDistance) &&
                    Number.isFinite(travelledDistance) &&
                    travelledDistance !== 0
                        ? (size.width * accumulateDistance) / travelledDistance
                        : 0

                setCursorPosition({ x, y: 0 })

                if (reCenter === true) map.setCenter({ lat: event.lat, lng: event.lng })
            }
        })
    }

    const _handleDraggableOnStop = (event, ui, width): void => {
        const active = getDrillDowneventActive(event, ui, width)
        const position = { lat: active.lat, lng: active.lng }
        selectPolyline(String(active.id))
        emitter.emit({ event: active })
        if (!marker) {
            _createMarker(event, false)
        } else {
            marker.current?.setPosition(position)
        }
    }

    const settingParameters = (lookups): void => {
        let aux = referenceLineLocal
        const lookupsNotHasReferenceLine =
            referenceLineLocal && !lookups.find(ele => ele.key === referenceLineLocal.key)

        if (lookupsNotHasReferenceLine) {
            aux = { key: undefined, value: undefined }
            setReferenceLineLocal(undefined)
        }

        emitter.emit({ lookups, referenceLine: aux })

        if (lookups === undefined) setLookups([])
        else setLookups(lookups)
    }

    const { createMarkers } = useMarkers(map)
    const [lookups, setLookups] = useState([])
    const [travelledDistance, setTravelledDistance] = useState(0)
    const [collapsed, setCollaped] = useState(false)
    const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
    const [mainBarValues, setMaiBarValues] = useState<BarValue[]>([])
    const [referenceLineLocal, setReferenceLineLocal] = useState<Lookup>(referenceLine)
    const refWrapper = useRef()

    const sizeWrapperInformation = useSize(refWrapper)

    const size = useSize(document.getElementById('drill-down-bar'))

    useEffect(() => {
        intervalDistance = calcIntervalDistance(events)
        const travelledDistance = calcTravelledDistance(events)
        const barValues = buildMainBarValues(paramsEvents?.gradientColorStart, paramsEvents?.gradientColorEnd)

        setMaiBarValues(barValues)
        setTravelledDistance(travelledDistance)
    }, [events, sizeWrapperInformation?.width]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setReferenceLineLocal(referenceLine)
    }, [referenceLine])

    useDeepCompareEffect(() => {
        if (paramsEvents?.parametersDefault) {
            const lookups = lookupEntries?.filter(ele => {
                return paramsEvents?.parametersDefault.includes(ele.key)
            })

            if (lookups === undefined) {
                setLookups([])
            } else {
                emitter?.emit({ lookups })
                setLookups(lookups)
            }
        }
    }, [paramsEvents?.parametersDefault]) // eslint-disable-line react-hooks/exhaustive-deps

    //CHECK HERE TO CREATE NEW MARKER
    emitter?.useSubscription(({ event, reCenter = true }) => {
        if (event) {
            _createMarker(event, reCenter)
        }
    })

    return (
        <WrapperTripsInformation ref={refWrapper}>
            <WrapperTitle>
                <div style={{ flex: 1 }}>
                    <span data-testid="title" className="parameters-information-title">
                        {i18n(title)}
                    </span>
                </div>

                {paramsEvents?.kpis?.map(({ field, title, icon }, index) => {
                    return (
                        <div className="parameters-information-item" key={index}>
                            {icon && (
                                <KpiIcon>
                                    <div>
                                        <i
                                            data-testid={'kpi-icon-' + index}
                                            className={icon + ' parameters-information-kpi-icon'}
                                        />
                                    </div>
                                </KpiIcon>
                            )}

                            <div>
                                <span data-testid={'kpi-title-' + index} className="parameters-information-kpi-title">
                                    {i18n(title)}:
                                </span>
                                <span data-testid={'kpi-value-' + index} className="parameters-information-kpi-field">
                                    {paramsEvents?.[field]}
                                </span>
                            </div>
                        </div>
                    )
                })}
            </WrapperTitle>

            <Steps unity="Km" steps={steps} maxValue={travelledDistance} />

            <Bar
                data-testid="test-bar-referenceline"
                values={mainBarValues}
                cursorPosition={cursorPosition}
                handleDraggableOnDrag={_handleDraggableOnDrag}
                handleDraggableOnStop={_handleDraggableOnStop}
            />

            <Divider />

            {lookups
                .filter(lookup => lookup.key === referenceLineLocal?.key)
                .map((lookup, index) => {
                    return (
                        <div key={index} style={{ marginTop: 16 }}>
                            <Bar
                                title={lookup?.key}
                                active={true}
                                paramsEvents={paramsEvents}
                                values={toBarValue(lookup, events)}
                                cursorPosition={cursorPosition}
                                handleDraggableOnDrag={_handleDraggableOnDrag}
                                handleDraggableOnStop={_handleDraggableOnStop}
                            />
                        </div>
                    )
                })}

            <FieldsHidden hidden={!collapsed || lookups.length == 0}>
                <div style={{ marginRight: 16 }}>
                    <i className="far fa-eye-slash" style={{ marginRight: 4 }}></i>
                    {i18n('Hidden')}:
                </div>

                {lookups
                    .filter(lookup => lookup.key !== referenceLineLocal?.key)
                    .map(({ key }) => {
                        return (
                            <div key={key} className="fields-hidden-item">
                                {key}
                            </div>
                        )
                    })}
            </FieldsHidden>

            <ToggleBar
                data-testid="test-tooglebar"
                closed={collapsed}
                height={88 * (referenceLineLocal ? lookups.length - 1 : lookups.length)}
            >
                {lookups
                    .filter(lookup => lookup.key !== referenceLineLocal?.key)
                    .map((lookup, index) => {
                        return (
                            <Bar
                                key={index}
                                title={lookup?.key}
                                paramsEvents={paramsEvents}
                                values={toBarValue(lookup, events)}
                                cursorPosition={cursorPosition}
                                handleDraggableOnDrag={_handleDraggableOnDrag}
                                handleDraggableOnStop={_handleDraggableOnStop}
                            />
                        )
                    })}
            </ToggleBar>

            <WrapperActions>
                <ModalSettingsParameters
                    initialValues={lookups}
                    lookupEntries={lookupEntries}
                    callback={settingParameters}
                />

                {lookups.length > 0 && (
                    <BtnShowHide onClick={() => setCollaped(!collapsed)}>
                        <span>{i18n(collapsed ? 'Show' : 'Hide')}</span>
                        <i className={`far ${collapsed ? 'fa-chevron-down' : 'fa-chevron-up'}`}></i>
                    </BtnShowHide>
                )}
            </WrapperActions>
        </WrapperTripsInformation>
    )
}

export default ParametersInformation

const WrapperTripsInformation = styled.div`
    padding: 0 16px;
`

const WrapperTitle = styled.div`
    display: flex;
    margin: 12px 0;

    .parameters-information-item {
        font-size: 11px;
        margin-right: 16px;
        display: flex;
        color: ${({ theme }) => theme['gray-600']};
    }

    .parameters-information-title {
        color: ${({ theme }) => theme['gray-800']};
        font-size: 16px;
        font-weight: 600;
    }

    .parameters-information-kpi-title {
        margin-right: 4px;
        font-size: 13px;
        font-weight: normal;
        color: ${({ theme }) => theme['gray-700']};
    }

    .parameters-information-kpi-field {
        font-size: 12px;
        font-weight: 600;
        color: ${({ theme }) => theme['gray-800']};
    }
`

const KpiIcon = styled.div`
    height: 20px;
    width: 20px;
    font-size: 10px;
    font-weight: normal;
    color: ${({ theme }) => theme['gray-700']};
    background: ${({ theme }) => theme['gray-200']};
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
`

const WrapperActions = styled.div`
    height: 30px;
    display: flex;
    font-size: 13px;
    font-weight: 600;
    margin-top: 16px;
    color: ${({ theme }) => theme['blue-700']};

    div {
        cursor: pointer;
    }

    .fa-chevron-up,
    .fa-chevron-down {
        margin-left: 8px;
    }

    .fa-cog {
        margin-right: 8px;
    }
`

const Divider = styled.div`
    border: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
`

const ToggleBar = styled.div`
    margin-top: 12px;
    /* height: ${({ height, closed }) =>
        closed
            ? 0
            : `${height}px`};   TODO - To use transition have to solve problem with legends lines break and your size isn't compute to total height       */ 
    height: ${({ height, closed }) => (closed ? 0 : `auto`)};
    transition-duration: 1s;
    transition-property: height;
    overflow: hidden;
`

const FieldsHidden = styled.div`
    display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
    margin: 12px 0;
    font-size: 11px;
    justify-content: center;
    color: ${({ theme }) => theme['gray-600']};

    .fields-hidden-item {
        margin-right: 16px;
        color: ${({ theme }) => theme['gray-700']};
    }
`

const BtnShowHide = styled.div`
    color: ${({ theme }) => theme['blue-500']};
`
